import React, {useCallback} from 'react';
import {SelectedOptions, useServerProps} from '@shopify/hydrogen';
import {
  Menu,
  MenuItem,
  MoneyV2,
  ProductVariant,
  UserError,
} from '@shopify/hydrogen/storefront-api-types';

// @ts-expect-error types not available
import typographicBase from 'typographic-base';
import {
  BOOST_FILTER_PREFIX,
  FILTER_TYPES,
  PASSWORD_MIN_LENGTH,
  SORT_OPTIONS,
} from '~/lib/const';
import {FilterOption, FilterStateType} from '~/types';
import {TypedObject} from '@sanity/types';

/**
 * This is a hack until we have better built-in primitives for
 * causing server components to re-render.
 *
 * @returns function when called will cause the current page to re-render on the server
 */
export function useRenderServerComponents() {
  const {serverProps, setServerProps} = useServerProps();

  return useCallback(() => {
    setServerProps('renderRsc', !serverProps.renderRsc);
  }, [serverProps, setServerProps]);
}

export function missingClass(string?: string, prefix?: string) {
  if (!string) {
    return true;
  }

  const regex = new RegExp(` ?${prefix}`, 'g');
  return string.match(regex) === null;
}

export function formatText(input?: string | React.ReactNode) {
  if (!input) {
    return;
  }

  if (typeof input !== 'string') {
    return input;
  }

  return typographicBase(input, {locale: 'en-us'}).replace(
    /\s([^\s<]+)\s*$/g,
    '\u00A0$1',
  );
}

export function isNewArrival(date: string, daysOld = 30) {
  return (
    new Date(date).valueOf() >
    new Date().setDate(new Date().getDate() - daysOld).valueOf()
  );
}

export function isDiscounted(price: MoneyV2, compareAtPrice: MoneyV2) {
  const priceValue = +(price?.amount ?? 0);
  const compareAtPriceValue = +(compareAtPrice?.amount ?? 0);

  // Check if the difference between the prices is greater than 1%
  const isBetterThan1Percent =
    (compareAtPriceValue - priceValue) / compareAtPriceValue > 0.01;

  if (isBetterThan1Percent) {
    return true;
  }

  return false;
}

export function getExcerpt(text: string) {
  const regex = /<p.*>(.*?)<\/p>/;
  const match = regex.exec(text);
  return match?.length ? match[0] : text;
}

function resolveToFromType(
  {
    customPrefixes,
    pathname,
    type,
  }: {
    customPrefixes: Record<string, string>;
    pathname?: string;
    type?: string;
  } = {
    customPrefixes: {},
  },
) {
  if (!pathname || !type) return '';

  /*
    MenuItemType enum
    @see: https://shopify.dev/api/storefront/unstable/enums/MenuItemType
  */
  const defaultPrefixes = {
    BLOG: 'blogs',
    COLLECTION: 'collections',
    COLLECTIONS: 'collections', // Collections All (not documented)
    FRONTPAGE: 'frontpage',
    HTTP: '',
    PAGE: 'pages',
    CATALOG: 'collections/all', // Products All
    PRODUCT: 'products',
    SEARCH: 'search',
    SHOP_POLICY: 'policies',
  };

  const pathParts = pathname.split('/');
  const handle = pathParts.pop() || '';
  const routePrefix: Record<string, string> = {
    ...defaultPrefixes,
    ...customPrefixes,
  };

  switch (true) {
    // special cases
    case type === 'FRONTPAGE':
      return '/';

    case type === 'ARTICLE': {
      const blogHandle = pathParts.pop();
      return routePrefix.BLOG
        ? `/${routePrefix.BLOG}/${blogHandle}/${handle}/`
        : `/${blogHandle}/${handle}/`;
    }

    case type === 'COLLECTIONS':
      return `/${routePrefix.COLLECTIONS}`;

    case type === 'SEARCH':
      return `/${routePrefix.SEARCH}`;

    case type === 'CATALOG':
      return `/${routePrefix.CATALOG}`;

    // common cases: BLOG, PAGE, COLLECTION, PRODUCT, SHOP_POLICY, HTTP
    default:
      return routePrefix[type]
        ? `/${routePrefix[type]}/${handle}`
        : `/${handle}`;
  }
}

/*
  Parse each menu link and adding, isExternal, to and target
*/
function parseItem(customPrefixes = {}) {
  return function (item: MenuItem): EnhancedMenuItem {
    if (!item?.url || !item?.type) {
      console.warn('Invalid menu item.  Must include a url and type.');
      // @ts-ignore
      return;
    }

    // extract path from url because we don't need the origin on internal to attributes
    const {pathname} = new URL(item.url);

    /*
      Currently the MenuAPI only returns online store urls e.g — xyz.myshopify.com/..
      Note: update logic when API is updated to include the active qualified domain
    */
    const isInternalLink = /\.myshopify\.com/g.test(item.url);

    const parsedItem = isInternalLink
      ? // internal links
        {
          ...item,
          isExternal: false,
          target: '_self',
          to: resolveToFromType({type: item.type, customPrefixes, pathname}),
        }
      : // external links
        {
          ...item,
          isExternal: true,
          target: '_blank',
          to: item.url,
        };

    return {
      ...parsedItem,
      items: item.items?.map(parseItem(customPrefixes)),
    };
  };
}

export interface EnhancedMenuItem extends MenuItem {
  to: string;
  target: string;
  isExternal?: boolean;
  items: EnhancedMenuItem[];
}

export interface EnhancedMenu extends Menu {
  items: EnhancedMenuItem[];
}

/*
  Recursively adds `to` and `target` attributes to links based on their url
  and resource type.
  It optionally overwrites url paths based on item.type
*/
export function parseMenu(menu: Menu, customPrefixes = {}): EnhancedMenu {
  if (!menu?.items) {
    console.warn('Invalid menu passed to parseMenu');
    // @ts-ignore
    return menu;
  }

  return {
    ...menu,
    items: menu.items.map(parseItem(customPrefixes)),
  };
}

export function getApiErrorMessage(
  field: string,
  data: Record<string, any>,
  errors: UserError[],
) {
  if (errors?.length) return errors[0].message ?? errors[0];
  if (data?.[field]?.customerUserErrors?.length)
    return data[field].customerUserErrors[0].message;
  return null;
}

export function statusMessage(status: string) {
  const translations: Record<string, string> = {
    ATTEMPTED_DELIVERY: 'Attempted delivery',
    CANCELED: 'Canceled',
    CONFIRMED: 'Confirmed',
    DELIVERED: 'Delivered',
    FAILURE: 'Failure',
    FULFILLED: 'Fulfilled',
    IN_PROGRESS: 'In Progress',
    IN_TRANSIT: 'In transit',
    LABEL_PRINTED: 'Label printed',
    LABEL_PURCHASED: 'Label purchased',
    LABEL_VOIDED: 'Label voided',
    MARKED_AS_FULFILLED: 'Marked as fulfilled',
    NOT_DELIVERED: 'Not delivered',
    ON_HOLD: 'On Hold',
    OPEN: 'Open',
    OUT_FOR_DELIVERY: 'Out for delivery',
    PARTIALLY_FULFILLED: 'Partially Fulfilled',
    PENDING_FULFILLMENT: 'Pending',
    PICKED_UP: 'Displayed as Picked up',
    READY_FOR_PICKUP: 'Ready for pickup',
    RESTOCKED: 'Restocked',
    SCHEDULED: 'Scheduled',
    SUBMITTED: 'Submitted',
    UNFULFILLED: 'Unfulfilled',
  };
  try {
    return translations?.[status];
  } catch (error) {
    return status;
  }
}

export function emailValidation(email: HTMLInputElement) {
  if (email.validity.valid) return null;

  return email.validity.valueMissing
    ? 'Please enter an email'
    : 'Please enter a valid email';
}

export function passwordValidation(password: HTMLInputElement) {
  if (password.validity.valid) return null;

  if (password.validity.valueMissing) {
    return 'Please enter a password';
  }

  return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
}

// Checks if it is empty array or if it is empty string or if it is null/undefined
export function isEmpty(variable: any) {
  if (typeof variable === 'string' && variable === '') return true;
  if (Array.isArray(variable) && variable.length === 0) return true;

  return variable == null;
}

export function formatDate(date: string, locale?: string) {
  // if locale undefined, function will use local timezone.
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getProductLabelFromTag(tags?: string[]) {
  const labelTag = tags?.find((tag) => tag.startsWith('LABEL:'));
  if (labelTag == null) return null;

  const [key, value, color, bgColor] = labelTag.split(':');
  return {
    key,
    value,
    color: color != null && !color.startsWith('#') ? `#${color}` : color,
    bgColor:
      bgColor != null && !bgColor.startsWith('#') ? `#${bgColor}` : bgColor,
  };
}

export function getProductPromoHeadlineFromTag(tags: string[]) {
  // e.g 'PROMO:PROMOTIONAL HEADLINE:10:#1EA540',
  if (tags == null) return null;
  const configs = tags
    .filter((tag) => tag.startsWith('PROMO:'))
    .map((tag) => {
      const [key, value, order, color] = tag.split(':');
      return {
        key,
        value,
        order: order != null ? Number(order) : undefined,
        color: color != null && !color.startsWith('#') ? `#${color}` : color,
      };
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return configs;
}

export const isObject = (value: any): boolean => {
  return typeof value === 'object' && !Array.isArray(value) && value != null;
};

export function getFilterAndSortFromQuery(requestUrl: string | URL) {
  const url = new URL(requestUrl);
  const sort = url.searchParams.get('sort') ?? SORT_OPTIONS[0].value;

  let searchParams = [...url.searchParams];

  try {
    // When returning from PDP page, the search params are stored in the state
    if (
      searchParams &&
      searchParams.length > 0 &&
      searchParams[0].length > 0 &&
      searchParams[0][0] === 'state'
    ) {
      const state = JSON.parse(searchParams[0][1]);
      // Dummy URL just to parse the search params
      const newURL = new URL(`http://localhost${state.search}`);

      searchParams = [...newURL.searchParams];
    }
  } catch (error) {
    console.error(error);
  }

  const filters = Object.fromEntries(
    searchParams
      .filter(([k]) => k.startsWith(BOOST_FILTER_PREFIX))
      .map(([k, v]) => {
        // Remove any double escape backslash
        // used in vercel.json to escape `:` but it is still in the url even though escaping works.
        const _v = decodeURIComponent(v).replace(/\\/g, '');
        return [k, _v];
      }),
  );

  const filtersDefault = Object.fromEntries(
    Object.entries(filters).map(([k, v]) => {
      const key = k.split('[]')[0];
      let value: string | string[] | {min: string; max: string} = v;
      if (
        key === 'pf_p_price' ||
        (!key.includes('opt_size') && key.includes('_size'))
      ) {
        const [min, max] = v.split(':');
        value = {min, max};
      } else {
        value = v.split(',');
        value = value.length === 1 ? value[0] : value;
      }
      return [key, value];
    }),
  ) as FilterStateType;

  const isThereFilter = Object.entries(filtersDefault).length > 0;
  return {sort, filters, filtersDefault, isThereFilter, pathname: url.pathname};
}

export function preventDefault(event: React.SyntheticEvent<HTMLElement>) {
  event.preventDefault();
}

export function parseFilterToSearchParams({
  params,
  filterOptions,
  key,
  value,
}: {
  params: URLSearchParams;
  filterOptions: FilterOption[];
  key: string;
  value: string | string[] | undefined;
}) {
  if (value == null || (Array.isArray(value) && value.length === 0)) {
    params.delete(key);
  } else {
    if (key.includes('_size') && !Array.isArray(value)) {
      const [min, max] = value.split(':');

      const options =
        filterOptions.find((o) => key.includes(o.filterType))?.options ?? [];
      const filteredOptions = options.filter(
        (o) =>
          parseFloat(o.value) >= parseFloat(min) &&
          parseFloat(o.value) <= parseFloat(max),
      );

      if (filteredOptions.length > 0) {
        for (const value of filteredOptions) {
          params.append(key, value.value);
        }
      } else {
        // if no options are found, setting an invalid option to return an empty list
        params.set(key, value);
      }
    } else if (key.includes('_color') && Array.isArray(value)) {
      for (const v of value) {
        const filterOption = filterOptions.find(
          (o) => o.type === FILTER_TYPES.COLOR,
        );

        if (filterOption) {
          const filteredOptions = filterOption.options?.find(
            (o) => o.value === v,
          );

          if (filteredOptions && filteredOptions.values) {
            for (const value of filteredOptions.values) {
              params.append(key, value);
            }
          } else {
            params.append(key, filteredOptions?.value ?? '');
          }
        }
      }
    } else {
      if (Array.isArray(value)) {
        for (const v of value) {
          params.append(key, v);
        }
      } else {
        if (value.includes(',')) {
          const values = value.split(',');
          for (const v of values) {
            params.append(key, v);
          }
        } else {
          params.set(key, value);
        }
      }
    }
  }
}

export function generateUUID() {
  // Public Domain/MIT
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getOptions(variants: ProductVariant[]) {
  const map = variants.reduce((memo, variant) => {
    if (!variant.selectedOptions) {
      throw new Error(`'getOptions' requires 'variant.selectedOptions'`);
    }

    // Don't include discontinued variants as options.
    if (
      variant.quantityAvailable! > 0 ||
      variant.discontinued?.value != 'true'
    ) {
      variant?.selectedOptions?.forEach((opt) => {
        memo[opt?.name ?? ''] = memo[opt?.name ?? ''] || new Set();
        memo[opt?.name ?? ''].add(opt?.value ?? '');
      });
    }

    return memo;
  }, {} as Record<string, Set<string>>);

  return Object.keys(map).map((option) => {
    return {
      id: option,
      name: option,
      values: Array.from(map[option]),
    };
  });
}

export function getDiscountedOptions(
  variants: ProductVariant[],
): {name: string; value: string}[] {
  const discountedOptions = new Set<string>();

  variants.forEach((variant) => {
    if (
      variant.priceV2 &&
      variant?.compareAtPriceV2 &&
      isDiscounted(variant.priceV2, variant.compareAtPriceV2)
    ) {
      variant.selectedOptions.forEach((option) => {
        const optionKey = `${option.name}:${option.value}`; // Create a unique key for each option
        discountedOptions.add(optionKey);
      });
    }
  });

  // Transform the Set into a grouped object
  const groupedOptions: Record<string, string[]> = {};

  Array.from(discountedOptions).forEach((optionKey) => {
    const [name, value] = optionKey.split(':');
    if (!groupedOptions[name]) {
      groupedOptions[name] = [];
    }
    groupedOptions[name].push(value);
  });

  return groupedOptions;
}

/**
 * Get a list of available options that has stock based on the selected options
 */
export function getAvailableOptions(
  variants: ProductVariant[],
  selectedOptions: SelectedOptions | undefined,
  options?: Array<{
    id: string;
    name: string;
    values: string[];
  }>,
) {
  // Map the options to a key value pair
  const map =
    options?.reduce((memo, option) => {
      memo[option.name ?? ''] = new Set(option.values ?? []);
      return memo;
    }, {} as Record<string, Set<string>>) ?? {};

  const newMap: Record<string, string[]> = {};
  const selectedOptionsKeys = Object.keys(selectedOptions ?? {}).filter((o) =>
    options?.find((op) => op.name === o),
  );

  // If the product only has zero or one option we can just check the variants
  if (selectedOptionsKeys.length <= 1) {
    for (const variant of variants) {
      if (!variant.availableForSale) {
        for (const option of variant.selectedOptions) {
          if (!newMap[option.name]) {
            newMap[option.name] = [];
          }
        }

        continue;
      }

      for (const option of variant.selectedOptions) {
        if (!newMap[option.name]) {
          newMap[option.name] = [];
        }

        // This map will have only available options
        newMap[option.name].push(option.value);
      }
    }

    return newMap;
  }

  // Clone the map to avoid mutating the original
  for (const key of Object.keys(map)) {
    newMap[key] = [...map[key]];
  }

  for (const key of Object.keys(map)) {
    const currentValue = selectedOptions?.[key];

    if (!currentValue) {
      continue;
    }

    // Get the variants that has the selected options
    // And get the other options that are available
    const optionVariants = variants
      ?.filter(
        (v) =>
          v.selectedOptions?.find((o) => o.name === key)?.value ===
          currentValue,
      )
      .map((v) => ({
        options: v.selectedOptions?.filter((o) => o.name !== key),
        availableForSale: v.availableForSale,
      }));

    for (const mapKey of Object.keys(map)) {
      if (mapKey === key) {
        continue;
      }

      const newValues: string[] = [];

      for (const optionVariant of optionVariants) {
        for (const op of optionVariant.options) {
          if (op.name === mapKey && optionVariant.availableForSale) {
            newValues.push(op.value);
          }
        }
      }

      newMap[mapKey] = newValues;
    }
  }

  return newMap;
}

export function getMissingOptionsInSearchParams(
  params: URLSearchParams,
  variant?: ProductVariant,
  partVariants?: ProductVariant[][],
) {
  const searchParams = new URLSearchParams(params.toString());
  const newOptions: Record<string, string> = {};
  const options = [...(variant?.selectedOptions ?? [])];

  for (const part of partVariants ?? []) {
    options.push(
      ...(part.find((v) => v.availableForSale)?.selectedOptions ?? []),
    );
  }

  for (const option of options) {
    if (option?.name && option.value) {
      if (!searchParams.get(encodeURIComponent(option.name.toLowerCase()))) {
        newOptions[option.name] = option.value;
      }
    }
  }

  return newOptions;
}

export function hashCode(string: string) {
  let hash = 0;

  for (let i = 0; i < string.length; i++) {
    const code = string.charCodeAt(i);
    hash = (hash << 5) - hash + code;
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash;
}

export function findVariantBasedOnSelectedOptions(
  variants: ProductVariant[] | undefined,
  selectedOptions: SelectedOptions | undefined,
) {
  return variants?.find((v) => {
    let variantFound = 0;

    for (const option of v.selectedOptions) {
      if (selectedOptions?.[option.name] === option.value) {
        variantFound += 1;
      }
    }

    return variantFound === v.selectedOptions.length;
  });
}

export function getFullTextFromRichText(copy?: TypedObject[]) {
  if (!Array.isArray(copy)) {
    console.error(
      'getFullTextFromRichText: rich text must be array of element.',
    );
    return '';
  }

  return copy.reduce((acc, item) => {
    // @ts-ignore
    item.children?.forEach((i) => {
      if (i.text?.length > 0) {
        acc += i.text;
      }
    });
    return acc;
  }, '');
}

// For some reason Hydrogen v1 puts query into `state` field of the url search params
export function getSearchParam(searchParams: URLSearchParams, key: string) {
  try {
    // @ts-ignore
    const state = JSON.parse(searchParams.get('state'));
    const searchString = state['search'];
    const searchParamsFromState = new URLSearchParams(searchString);
    return searchParamsFromState.get(key);
  } catch (error) {
    return searchParams.get(key);
  }
}

export function getNumberID(id: string | number): number {
  if (typeof id === 'string') {
    if (isNaN(Number(id))) {
      return Number(id.split('/').pop());
    } else {
      return Number(id);
    }
  }

  return id;
}

export function preventBodyScroll(blockScroll: boolean) {
  if (blockScroll) {
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.getElementsByTagName('html')[0].style.overflow = 'hidden';

    return;
  }

  document.body.style.width = 'auto';
  document.body.style.height = 'auto';
  document.body.style.overflow = 'initial';
  document.body.style.position = 'inherit';
  document.body.style.touchAction = 'auto';
  document.body.style.overscrollBehavior = 'auto';
  document.getElementsByTagName('html')[0].style.overflow = 'inherit';
  document.getElementsByTagName('html')[0].style.height = 'auto';
}

export function sortByStringProperty<T>(
  propName: keyof T,
): (a: T, b: T) => number {
  return (a, b) => {
    const valA = a[propName];
    const valB = b[propName];

    if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
      return Number(valA) - Number(valB);
    }

    const strA = String(valA).toUpperCase();
    const strB = String(valB).toUpperCase();
    if (strA < strB) {
      return -1;
    }
    if (strA > strB) {
      return 1;
    }
    return 0;
  };
}

export const removeHTmlTagsFromString = (string: string) =>
  string.replace(/<[^>]*>?/gm, '');
